'use client';

import { useEffect } from 'react';
import { markGtmReady } from '~/helpers/datalayer';

/**
 * GtmConsentLoader — OneTrust-consent-driven Google Tag Manager loader.
 *
 * WHY THIS EXISTS:
 * In this Next.js app, OneTrust cannot auto-unblock GTM because there are no
 * <script type="text/plain" class="optanon-category-*"> tags in the DOM.
 * Next.js hydrates scripts after OneTrust has already scanned the page, so the
 * standard auto-blocking approach does not work. Instead, we explicitly inject
 * the GTM script after verifying that the required consent category (C0003 —
 * Functional) is active.
 *
 * The GTM container ID is read from the API.
 * If the variable is not set, this component renders nothing and does nothing.
 *
 * @returns {null} This component renders no DOM elements.
 */

/** OneTrust category required before GTM may load (Functional). */
const CONSENT_CATEGORY = 'C0013';

/** Unique DOM id used to identify the injected GTM script element. */
const GTM_SCRIPT_ID = 'gtm-consent-loader-script';

/**
 * WHY DEDUPE IS NEEDED:
 * OneTrust fires multiple events (OneTrustGroupsUpdated, OneTrustLoaded,
 * OptanonLoaded) and can fire them more than once during a single page session
 * (e.g. when the user interacts with the consent banner). Without a module-level
 * guard, the GTM bootstrap snippet and <script> tag would be injected multiple
 * times, causing duplicate tracking hits and console errors.
 *
 * @type {boolean}
 */
let gtmInjected = false;

/**
 * Checks whether the required OneTrust consent category is currently active.
 * Inspects both `window.OnetrustActiveGroups` and `window.OptanonActiveGroups`
 * because different OneTrust SDK versions / configurations populate different
 * globals.
 *
 * @returns {boolean} `true` when CONSENT_CATEGORY is present in either global.
 */
function hasConsent() {
  const onetrust =
    // @ts-ignore – OneTrust globals are not in the default Window type
    (typeof window !== 'undefined' && window.OnetrustActiveGroups) || '';
  const optanon =
    // @ts-ignore
    (typeof window !== 'undefined' && window.OptanonActiveGroups) || '';

  return (
    onetrust.indexOf(CONSENT_CATEGORY) !== -1 ||
    optanon.indexOf(CONSENT_CATEGORY) !== -1
  );
}

/**
 * Injects the GTM bootstrap snippet exactly once.
 *
 * 1. Initialises `window.dataLayer` (if it doesn't exist yet).
 * 2. Pushes the mandatory `{ "gtm.start", event: "gtm.js" }` entry.
 * 3. Appends the GTM loader `<script>` to `<head>`.
 *
 * @param {string} gtmId - The GTM container ID (e.g. "GTM-XXXXXX").
 * @returns {void}
 */
function injectGtm(gtmId) {
  // Module-level dedupe guard (survives across re-renders / hot-reloads)
  if (gtmInjected) {
    markGtmReady();
    return;
  }

  // DOM-level dedupe guard (catches edge-case where another code path already injected the tag)
  if (document.getElementById(GTM_SCRIPT_ID)) {
    gtmInjected = true;
    markGtmReady();
    return;
  }

  // Initialise dataLayer and push the GTM bootstrap event BEFORE the script loads
  // @ts-ignore
  window.dataLayer = window.dataLayer || [];
  // @ts-ignore
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  // Preserve original page location for SPA tracking accuracy
  // @ts-ignore
  window.dataLayer.push({
    originalLocation:
      document.location.protocol +
      '//' +
      document.location.hostname +
      document.location.pathname +
      document.location.search,
  });

  var script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId;

  script.addEventListener('load', () => {
    gtmInjected = true;
    markGtmReady();
  });

  document.head.appendChild(script);

  gtmInjected = true;
}

/**
 * Client component that loads Google Tag Manager only after OneTrust consent
 * for the required category has been granted.
 *
 * Mount this component once at the top of your component tree (e.g. in the
 * root layout / RendererWrapper) so it runs on every page.
 *
 * @param {Object} props
 * @param {string | undefined} props.googleTagManagerId
 * @returns {null}
 */
const GtmConsentLoader = ({ googleTagManagerId }) => {
  useEffect(() => {
    // Bail out entirely when no GTM ID is configured
    if (!googleTagManagerId) return;

    /** @type {string} */
    const id = googleTagManagerId;

    /**
     * Attempt to inject GTM if consent has been granted.
     * Called on mount (initial check) and whenever OneTrust fires a
     * consent-related event.
     */
    function tryLoad() {
      if (hasConsent()) {
        injectGtm(id);
      }
    }

    // --- Initial check -------------------------------------------------------
    // Handles page refresh / navigation when the consent cookie already exists
    // and OnetrustActiveGroups is populated before this effect runs.
    tryLoad();

    // --- Event listeners ------------------------------------------------------
    // OneTrust dispatches these custom events on `window` when consent state
    // changes or when the SDK finishes initialising:
    //   • OneTrustGroupsUpdated – fired whenever active groups change
    //   • OneTrustLoaded        – fired once when the OneTrust SDK is ready
    //   • OptanonLoaded         – legacy alias used by older OneTrust versions
    window.addEventListener('OneTrustGroupsUpdated', tryLoad);
    window.addEventListener('OneTrustLoaded', tryLoad);
    window.addEventListener('OptanonLoaded', tryLoad);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', tryLoad);
      window.removeEventListener('OneTrustLoaded', tryLoad);
      window.removeEventListener('OptanonLoaded', tryLoad);
    };
  }, [googleTagManagerId]);

  // This component is side-effect-only; it renders nothing.
  return null;
};

export default GtmConsentLoader;
